// Assessment page URL state and result filters: the open topic, tones (at any level or one level), review state, "has checks" and "low AI confidence" (at the chosen level) as typed search params, the rows they keep in catalogue order, counts per filter value, the active filter chips, and the previous and next visible topic around any topic.
import { z } from "zod";

import type { ReviewState, TopicResultRow, VerdictTone } from "@/api/__generated__";
import { zVerdictTone } from "@/api/__generated__/zod.gen";
import { booleanWithDefault, enumWithDefault, optionalList, optionalString } from "@/app/search";

import { LEVEL_LABELS, LEVELS, REVIEW_LABELS, TONE_LABELS, type LevelKey } from "../labels";

export const LEVEL_SCOPES = ["any", "wtg", "ppc", "poc"] as const;
export const REVIEW_FILTERS = ["all", "pending", "approved", "changed"] as const;

export type LevelScope = (typeof LEVEL_SCOPES)[number];
export type ReviewFilter = (typeof REVIEW_FILTERS)[number];

export const assessmentSearchSchema = z.object({
  topic: optionalString(),
  tone: optionalList(zVerdictTone),
  level: enumWithDefault(LEVEL_SCOPES, "any"),
  review: enumWithDefault(REVIEW_FILTERS, "all"),
  checks: booleanWithDefault(false),
  unsure: booleanWithDefault(false),
});

export const assessmentSearchDefaults = {
  level: "any",
  review: "all",
  checks: false,
  unsure: false,
} satisfies Partial<z.output<typeof assessmentSearchSchema>>;

export type AssessmentSearch = z.output<typeof assessmentSearchSchema>;
export type ResultFilters = Pick<AssessmentSearch, "tone" | "level" | "review" | "checks" | "unsure">;

function scopedLevels(scope: LevelScope): readonly LevelKey[] {
  return scope === "any" ? LEVELS : [scope];
}

export function rowTones(row: TopicResultRow, scope: LevelScope): Set<VerdictTone> {
  return new Set(scopedLevels(scope).map((level) => row[level].tone));
}

export function matchesTone(row: TopicResultRow, tones: readonly VerdictTone[], scope: LevelScope): boolean {
  if (tones.length === 0) {
    return true;
  }
  const present = rowTones(row, scope);
  return tones.some((tone) => present.has(tone));
}

export function isUnsure(row: TopicResultRow, scope: LevelScope): boolean {
  return scopedLevels(scope).some((level) => row[level].confidence === "low");
}

export function matchesReview(row: TopicResultRow, review: ReviewFilter): boolean {
  return review === "all" || row.review_state === review;
}

export function filterRows(rows: readonly TopicResultRow[], filters: ResultFilters): TopicResultRow[] {
  return [...rows]
    .sort((left, right) => left.ordinal - right.ordinal)
    .filter(
      (row) =>
        matchesTone(row, filters.tone ?? [], filters.level) &&
        matchesReview(row, filters.review) &&
        (!filters.checks || row.checks_count > 0) &&
        (!filters.unsure || isUnsure(row, filters.level)),
    );
}

export function toneCounts(rows: readonly TopicResultRow[], scope: LevelScope): Record<VerdictTone, number> {
  const counts: Record<VerdictTone, number> = {
    positive: 0,
    positive_soft: 0,
    info: 0,
    caution: 0,
    negative: 0,
    muted: 0,
  };
  for (const row of rows) {
    for (const tone of rowTones(row, scope)) {
      counts[tone] += 1;
    }
  }
  return counts;
}

export function reviewCounts(rows: readonly TopicResultRow[]): Record<ReviewState, number> {
  const counts: Record<ReviewState, number> = { pending: 0, approved: 0, changed: 0 };
  for (const row of rows) {
    counts[row.review_state] += 1;
  }
  return counts;
}

export function hasActiveFilters(filters: ResultFilters): boolean {
  return (filters.tone?.length ?? 0) > 0 || filters.review !== "all" || filters.checks || filters.unsure;
}

export interface ActiveFilter {
  id: string;
  label: string;
  patch: Partial<ResultFilters>;
}

export function activeFilters(filters: ResultFilters): ActiveFilter[] {
  const tones = filters.tone ?? [];
  const where = filters.level === "any" ? "" : ` at ${LEVEL_LABELS[filters.level].short}`;
  const chips: ActiveFilter[] = tones.map((tone) => ({
    id: `tone-${tone}`,
    label: `${TONE_LABELS[tone]}${where}`,
    patch: { tone: tones.filter((value) => value !== tone) },
  }));
  if (filters.review !== "all") {
    chips.push({
      id: "review",
      label: `Review: ${REVIEW_LABELS[filters.review]}`,
      patch: { review: "all" },
    });
  }
  if (filters.checks) {
    chips.push({ id: "checks", label: "Has checks", patch: { checks: false } });
  }
  if (filters.unsure) {
    chips.push({ id: "unsure", label: `Low AI confidence${where}`, patch: { unsure: false } });
  }
  return chips;
}

export function neighbours(
  ordered: readonly TopicResultRow[],
  visible: readonly TopicResultRow[],
  key: string,
): { previous: TopicResultRow | null; next: TopicResultRow | null } {
  const current = ordered.find((row) => row.key === key);
  if (current === undefined) {
    return { previous: null, next: null };
  }
  const before = visible.filter((row) => row.ordinal < current.ordinal);
  const after = visible.filter((row) => row.ordinal > current.ordinal);
  return { previous: before.at(-1) ?? null, next: after[0] ?? null };
}

export function nextPending(rows: readonly TopicResultRow[], key: string): TopicResultRow | null {
  const ordered = [...rows].sort((left, right) => left.ordinal - right.ordinal);
  const index = ordered.findIndex((row) => row.key === key);
  const pending = (row: TopicResultRow) => row.review_state === "pending" && row.key !== key;
  return ordered.slice(index + 1).find(pending) ?? ordered.slice(0, Math.max(index, 0)).find(pending) ?? null;
}
